import React from "react";
import { Routes, Route } from "react-router-dom";
import { Container, Box } from "@mui/material";
import { NavigationProvider } from "./contexts/NavigationContext";
import { AuthProvider } from "./contexts/AuthContext";
import { TenantProvider } from "./contexts/TenantContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AppLayout from "./layouts/AppLayout";
import DashboardPage from "./pages/DashboardPage";
import ResidentsPage from "./pages/ResidentsPage";
import LoginPage from "./pages/LoginPage";
import AdmissionsPage from "./pages/AdmissionsPage";
import ClinicalNotesPage from "./pages/ClinicalNotesPage";
import ProgressNotesPage from "./pages/ProgressNotesPage";
import ResidentProgressNotesPage from "./pages/ResidentProgressNotesPage";
import MdsAssessmentPage from "./pages/MdsAssessmentPage";
import HospiceAssessmentPage from "./pages/HospiceAssessmentPage";
import BillingPage from "./pages/BillingPage";
import TenantManagementPage from "./pages/TenantManagementPage";
import SuperAdminLoginPage from "./pages/SuperAdminLoginPage";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import ClientManagementPage from "./pages/ClientManagementPage";
import ClientFacilitiesPage from "./pages/ClientFacilitiesPage";
import PatientDiagnosisPage from "./pages/PatientDiagnosisPage";
import OrderMedicationPage from "./pages/OrderMedicationPage";
import UserManagementPage from "./pages/UserManagementPage";
import DownloadMarPage from "./pages/DownloadMarPage";
import ProfilePage from "./pages/ProfilePage";
import FacilitySettingsPage from "./pages/FacilitySettingsPage";
import UserSettingsPage from "pages/UserSettingsPage";
import AuditLogsPage from "pages/AuditLogsPage";
import LookupsPage from "pages/LookupsPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import MedicationSchedulePage from "./pages/MedicationSchedulePage";
import StockAvailabilityPage from "./pages/StockAvailabilityPage";
import CaregiverTasksPage from "./pages/CaregiverTasksPage";
import AttendanceManagementPage from "./pages/AttendanceManagementPage";
import { SettingsConfigPage } from "./pages/SettingsConfigPage";
import CareCommandCenterPage from "./pages/CareCommandCenterPage";
import CareRequestPage from "./pages/CareRequestPage";
import AdmissionInquiryPage from "./pages/AdmissionInquiryPage";
import StaffAssignmentPage from "./pages/StaffAssignmentPage";
import CareDocumentsPage from "./pages/CareDocumentsPage";
import CareReportsPage from "./pages/CareReportsPage";
import ReportsPage from "./pages/ReportsPage";
import ShiftHandoverPage from "./pages/ShiftHandoverPage";
import NetworkStatus from "./components/NetworkStatus";

function App() {
  return (
    <TenantProvider>
      <AuthProvider>
        <NavigationProvider>
          <NetworkStatus />
          <Routes>
            {/* Public routes - no auth required */}
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/super-admin/login"
              element={<SuperAdminLoginPage />}
            />

            {/* Hidden routes accessible by URL */}
            <Route
              path="/ResetPasswordPage"
              element={<ResetPasswordPage />}
            />
            <Route
              path="/resetpasswordpage"
              element={<ResetPasswordPage />}
            />
            <Route
              path="/reset-password"
              element={<ResetPasswordPage />}
            />

            {/* Protected routes requiring authentication */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <DashboardPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <DashboardPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/residents"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <ResidentsPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/clinical-notes"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", p: 0 }}
                      disableGutters
                    >
                      <ClinicalNotesPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/progress-notes"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <ProgressNotesPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/resident-progress-notes"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <ResidentProgressNotesPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admissions"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <AdmissionsPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/patient-diagnosis"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <PatientDiagnosisPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/emr"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", p: 0 }}
                      disableGutters
                    >
                      <ClinicalNotesPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/mds-assessment"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <MdsAssessmentPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/FacilitySettingsPage"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <FacilitySettingsPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/UserSettingsPage"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <UserSettingsPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hospice-assessment"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <HospiceAssessmentPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/billing"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <BillingPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/audit-logs"
              element={
                <ProtectedRoute allowedRoles={["ADMIN", "SUPER_ADMIN"]}>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <AuditLogsPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/lookups"
              element={
                <ProtectedRoute allowedRoles={["ADMIN", "SUPER_ADMIN"]}>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <LookupsPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/medication-orders"
              element={
                <ProtectedRoute allowedRoles={["ADMIN", "FACILITY_ADMIN", "SUPER_ADMIN"]}>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <OrderMedicationPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/download-mar"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <DownloadMarPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/medication-schedule"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <MedicationSchedulePage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/stock-availability"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <StockAvailabilityPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tenant-management"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <TenantManagementPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            {/* Super Admin Routes */}
            <Route
              path="/super-admin/dashboard"
              element={
                <ProtectedRoute allowedRoles={["SUPER_ADMIN"]}>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <SuperAdminDashboard />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/super-admin/clients"
              element={
                <ProtectedRoute allowedRoles={["SUPER_ADMIN"]}>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <ClientManagementPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/super-admin/clients/:clientId/facilities"
              element={
                <ProtectedRoute allowedRoles={["SUPER_ADMIN", "CLIENT_ADMIN"]}>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <ClientFacilitiesPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/users"
              element={
                <ProtectedRoute allowedRoles={["SUPER_ADMIN", "CLIENT_ADMIN"]}>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <UserManagementPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <ProfilePage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <CaregiverTasksPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/attendance-management"
              element={
                <ProtectedRoute allowedRoles={["ADMIN", "FACILITY_ADMIN", "SUPER_ADMIN"]}>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <AttendanceManagementPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute allowedRoles={["ADMIN", "SUPER_ADMIN", "CLIENT_ADMIN", "FACILITY_ADMIN"]}>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <SettingsConfigPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute allowedRoles={["ADMIN", "SUPER_ADMIN", "CLIENT_ADMIN", "FACILITY_ADMIN"]}>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <ReportsPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Care Management (CRM) Routes */}
            <Route
              path="/care/command-center"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, py: 2 }}
                    >
                      <CareCommandCenterPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/care/requests"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <CareRequestPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/care/admissions"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, py: 2 }}
                    >
                      <AdmissionInquiryPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            {/* 
            <Route
              path="/care/residents"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, py: 2 }}
                    >
                      <ResidentDirectoryPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            /> 
            */}
            <Route
              path="/care/staff-assignments"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, py: 2 }}
                    >
                      <StaffAssignmentPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/care/documents"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, py: 2 }}
                    >
                      <CareDocumentsPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/care/reports"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, py: 2 }}
                    >
                      <CareReportsPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/care/handover"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Container
                      maxWidth={false}
                      sx={{ maxWidth: "unset", px: 3, pt: 0, pb: 2 }}
                    >
                      <ShiftHandoverPage />
                    </Container>
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Roster Management Routes 
            {["/roster", "/roster/leave", "/roster/timesheets", "/roster/swaps", "/roster/payroll"].map((rpath) => (
              <Route
                key={rpath}
                path={rpath}
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <Container maxWidth={false} sx={{ maxWidth: "unset", px: 3, py: 2 }}>
                        <RosterManagementPage />
                      </Container>
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
            ))}
            */}
          </Routes>
        </NavigationProvider>
      </AuthProvider>
    </TenantProvider>
  );
}

export default App;